import { useState } from 'react'
import { toast } from 'sonner'
import { Link, useNavigate } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { LoadingButton } from '@/components/loading-button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertTriangle,
  BarChart3,
  Calendar,
  CalendarArrowUp,
  CheckCircle2,
  Check,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Download,
  MapPin,
  Copy,
  Package,
  Pencil,
  ReceiptText,
  Route,
  Scale,
  Truck,
  XCircle,
} from 'lucide-react'
import {
  useBookShipment,
  useCancelShipment,
  useCorrectShipmentStatus,
  useDuplicateShipment,
  useShipment,
} from '@/api/hooks'
import { BackButton } from '@/components/back-button'
import { EditShipmentSheet } from '@/components/edit-shipment-sheet'
import { EntityActivityBadge } from '@/components/entity-activity-badge'
import { ShipmentDateCard } from '@/components/shipment-dates'
import { ProductSummaryRow } from '@/components/product-summary-list'
import { ShipmentReferenceFields, ShipmentStatusEdit } from '@/components/shipment-status-edit'
import { shipmentStatusVariant } from '@/components/manual-status-options'
import { useAuth } from '@/hooks/use-auth'
import { formatApiErrorMessage } from '@/lib/api-error'
import { formatDateOnly, formatMoney, formatStatus, parseUTCDate } from '@/lib/utils'
import { usePageTitle } from '@/lib/page-title'
import { ComposeInput, ComposeTextarea } from '@/components/compose-field'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'

function trackingStatusStyle(status: string) {
  switch (status) {
    case 'delivered':
      return { dot: 'bg-green-500', badge: 'border-green-300 bg-green-50 text-green-700' }
    case 'in_transit':
    case 'shipped':
      return { dot: 'bg-blue-500', badge: 'border-blue-300 bg-blue-50 text-blue-700' }
    case 'pending':
      return { dot: 'bg-amber-500', badge: 'border-amber-300 bg-amber-50 text-amber-700' }
    case 'exception':
    case 'failed':
      return { dot: 'bg-red-500', badge: 'border-red-300 bg-red-50 text-red-700' }
    default:
      return { dot: 'bg-muted-foreground', badge: 'border-muted bg-muted/30 text-muted-foreground' }
  }
}

interface ShipmentDetailPageProps {
  shipmentId: string
}

export function ShipmentDetailPage({ shipmentId }: ShipmentDetailPageProps) {
  const { data: shipment, isLoading } = useShipment(shipmentId)
  usePageTitle(shipment?.ship_from_name && `Shipment from ${shipment.ship_from_name}`)
  const correctStatus = useCorrectShipmentStatus(shipment?.purchase_order?.uuid ?? '', shipmentId)
  const { getAccessToken } = useAuth()
  const [wwexDetailsOpen, setWwexDetailsOpen] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelConfirmText, setCancelConfirmText] = useState('')
  const [editOpen, setEditOpen] = useState(false)
  const cancelMutation = useCancelShipment(
    shipment?.purchase_order?.uuid ?? '',
    shipmentId,
  )
  const bookMutation = useBookShipment(
    shipment?.purchase_order?.uuid ?? '',
    shipmentId,
  )
  const duplicateMutation = useDuplicateShipment(shipment?.purchase_order?.uuid ?? '')
  const navigate = useNavigate()

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex items-center gap-1.5">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-20" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-7 w-64" />
          <div className="flex gap-2">
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-5 w-28" />
          </div>
        </div>
        <div className="grid gap-6 xl:grid-cols-2">
          <Skeleton className="h-40 w-full rounded-lg" />
          <Skeleton className="h-40 w-full rounded-lg" />
        </div>
        <div className="grid gap-6 xl:grid-cols-2">
          <Skeleton className="h-32 w-full rounded-lg" />
          <Skeleton className="h-32 w-full rounded-lg" />
        </div>
      </div>
    )
  }

  if (!shipment) {
    return (
      <div className="mx-auto max-w-7xl">
        <p className="text-muted-foreground">Shipment not found</p>
      </div>
    )
  }

  const po = shipment.purchase_order
  const order = po?.internal_order
  const project = order?.project
  const expectedPayable = shipment.expected_payable
  const payableAllocations = expectedPayable?.allocations ?? []
  const currentAllocations = payableAllocations.filter((allocation) =>
    ['proposed', 'confirmed'].includes(allocation.review_status),
  )
  const carrierInvoiceTotal = currentAllocations
    .reduce((total, allocation) => total + Number(allocation.allocation_amount), 0)
  const currentFreightVariance = [...(expectedPayable?.freight_variances ?? [])]
    .filter((variance) => !['superseded', 'void'].includes(variance.status))
    .sort((left, right) => right.id - left.id)[0]

  const title = shipment.ship_from_name
    ? `Shipment from ${shipment.ship_from_name}`
    : 'Shipment'

  const hasShipFromAddress =
    shipment.ship_from_name ||
    shipment.ship_from_address1 ||
    shipment.ship_from_city ||
    shipment.ship_from_state

  const hasShipToAddress =
    po?.ship_to_name ||
    po?.ship_to_company ||
    po?.ship_to_address1 ||
    po?.ship_to_city

  const pickupAccessorials = [
    shipment.inside_pickup && 'Inside Pickup',
    shipment.liftgate_pickup && 'Liftgate Pickup',
    shipment.residential_pickup && 'Residential Pickup',
    shipment.tradeshow_pickup && 'Tradeshow Pickup',
    shipment.construction_site_pickup && 'Construction Site Pickup',
  ].filter(Boolean) as string[]

  const deliveryAccessorials = [
    shipment.liftgate_required && 'Liftgate Required',
    shipment.appointment_required && 'Appointment Required',
    shipment.inside_delivery && 'Inside Delivery',
    shipment.residential_delivery && 'Residential Delivery',
    shipment.tradeshow_delivery && 'Tradeshow Delivery',
    shipment.construction_site_delivery && 'Construction Site Delivery',
    shipment.notify_before_delivery && 'Notify Before Delivery',
    shipment.hold_at_terminal && 'Hold At Terminal',
    shipment.sort_and_segregate && 'Sort & Segregate',
    shipment.protect_from_freeze && 'Protect From Freeze',
  ].filter(Boolean) as string[]

  // "Picked up" → carrier has physically taken the freight. Match the backend's
  // non-cancellable status set exactly (app/routers/dashboard/purchase_orders.py).
  const canCancelShipment = !['shipped', 'in_transit', 'delivered', 'cancelled'].includes(
    shipment.status,
  )

  const confirmPhrase = 'CANCEL'
  const canSubmitCancel =
    cancelReason.trim().length > 0 &&
    cancelConfirmText.trim().toUpperCase() === confirmPhrase &&
    !cancelMutation.isPending

  const handleConfirmCancel = () => {
    if (!canSubmitCancel) return
    cancelMutation.mutate(
      { shipmentId: shipment.id, reason: cancelReason.trim() },
      {
        onSuccess: (data) => {
          setCancelOpen(false)
          setCancelReason('')
          setCancelConfirmText('')
          const voided = Boolean(
            (data as { carrier_voided?: boolean } | undefined)?.carrier_voided,
          )
          toast.success(
            voided
              ? 'Shipment cancelled and carrier booking voided'
              : 'Shipment cancelled',
          )
        },
        onError: (err) => {
          toast.error(formatApiErrorMessage('Cancel failed', err))
        },
      },
    )
  }

  const handleDownloadDocument = async (type: 'bol' | 'label', filename: string) => {
    const token = await getAccessToken()
    if (!token) return
    const response = await fetch(
      `${API_BASE_URL}/dashboard/shipments/${shipmentId}/${type}`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
    if (!response.ok) return
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="space-y-1">
        <BackButton fallback={{ to: '/shipments' }} fallbackLabel="Back to Shipments" />

        {/* Breadcrumb */}
        <nav className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-muted-foreground">
          {project && (
            <>
              <Link
                to="/projects/$projectId"
                params={{ projectId: project.uuid }}
                className="truncate hover:text-foreground"
              >
                {project.name}
              </Link>
              <span>/</span>
            </>
          )}
          {po && (
            <>
              <Link
                to="/purchase-orders/$poId"
                params={{ poId: po.uuid }}
                className="hover:text-foreground"
              >
                PO #{po.po_number}
              </Link>
              <span>/</span>
            </>
          )}
          <span className="font-medium text-foreground">Shipment</span>
        </nav>
      </div>

      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {po ? (
              <ShipmentStatusEdit shipment={shipment} correct={correctStatus} />
            ) : (
              <Badge variant={shipmentStatusVariant(shipment.status)}>
                {formatStatus(shipment.status)}
              </Badge>
            )}
            {shipment.booked_by && (
              <Badge variant="outline">
                Booked by: {formatStatus(shipment.booked_by)}
              </Badge>
            )}
            <EntityActivityBadge entityType="shipment" entityId={shipment.id} />
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {!['delivered', 'cancelled'].includes(shipment.status) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              {shipment.status === 'pending' || shipment.status === 'ready_to_book'
                ? 'Edit'
                : 'Email policy'}
            </Button>
          )}
          {shipment.status === 'ready_to_book' && shipment.booked_by === 'us' && (
            <LoadingButton
              size="sm"
              loading={bookMutation.isPending}
              loadingText="Queuing..."
              onClick={() => {
                bookMutation.mutate(shipment.id, {
                  onSuccess: () =>
                    toast.success('Booking queued — label/BOL email will follow once carrier confirms'),
                  onError: (err) =>
                    toast.error(formatApiErrorMessage('Book failed', err)),
                })
              }}
            >
              <Truck className="mr-1.5 h-3.5 w-3.5" />
              Book Shipment
            </LoadingButton>
          )}
          {shipment.status !== 'pending' && (
            <LoadingButton
              variant="outline"
              size="sm"
              loading={duplicateMutation.isPending}
              loadingText="Duplicating..."
              onClick={() => {
                duplicateMutation.mutate(
                  { shipmentId: shipment.id },
                  {
                    onSuccess: (data) => {
                      toast.success('New pending shipment created — edit and book when ready')
                      const newUuid = (data as { uuid?: string } | undefined)?.uuid
                      if (newUuid) {
                        navigate({ to: '/shipments/$shipmentId', params: { shipmentId: newUuid } })
                      }
                    },
                    onError: (err) =>
                      toast.error(formatApiErrorMessage('Duplicate failed', err)),
                  },
                )
              }}
            >
              <Copy className="mr-1.5 h-3.5 w-3.5" />
              Duplicate
            </LoadingButton>
          )}
          {canCancelShipment && (
            <Button
              variant="outline"
              size="sm"
              className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 dark:border-red-900 dark:text-red-400 dark:hover:bg-red-950/40"
              onClick={() => {
                setCancelReason('')
                setCancelConfirmText('')
                setCancelOpen(true)
              }}
            >
              <XCircle className="mr-1.5 h-3.5 w-3.5" />
              Cancel Shipping
            </Button>
          )}
        </div>
      </div>

      {/* Dates & Logistics */}
      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Dates</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2">
              <ShipmentDateCard
                label="ASD (Actual Ship)"
                date={shipment.actual_ship_date}
                tone="asd"
                icon={<CheckCircle2 className="h-4 w-4" />}
              />
              <ShipmentDateCard
                label="ESD (Est. Ship)"
                date={shipment.estimated_ship_date}
                tone="esd"
                icon={<CalendarArrowUp className="h-4 w-4" />}
                strike={Boolean(shipment.actual_ship_date)}
              />
              <ShipmentDateCard
                label="EDD (Est. Delivery)"
                date={shipment.estimated_delivery_date}
                tone="edd"
                icon={<MapPin className="h-4 w-4" />}
              />
              {shipment.confirmed_ship_date && (
                <div className="flex items-start gap-2 rounded-md border px-3 py-2">
                  <Calendar className="mt-0.5 h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Confirmed Ship
                    </p>
                    <p className="text-sm font-medium">
                      {formatDateOnly(shipment.confirmed_ship_date)}
                    </p>
                  </div>
                </div>
              )}
              {shipment.pickup_date && (
                <div className="flex items-start gap-2 rounded-md border px-3 py-2">
                  <Truck className="mt-0.5 h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Pickup
                    </p>
                    <p className="text-sm font-medium">
                      {formatDateOnly(shipment.pickup_date)}
                      {(shipment.pickup_time_start || shipment.pickup_time_end) && (
                        <span className="text-muted-foreground font-normal">
                          {' '}
                          ({shipment.pickup_time_start || '?'} –{' '}
                          {shipment.pickup_time_end || '?'})
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              )}
              {!shipment.estimated_ship_date &&
                !shipment.confirmed_ship_date &&
                !shipment.actual_ship_date &&
                !shipment.pickup_date &&
                !shipment.estimated_delivery_date && (
                  <p className="text-sm text-muted-foreground">No dates set</p>
                )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Logistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2">
              {/* Carrier, tracking and references are click to edit (USA-1466); the booked service stays read-only. */}
              {po && <ShipmentReferenceFields shipment={shipment} correct={correctStatus} />}
              {po && (shipment.scac_code || shipment.service_level) && (
                <div>
                  <p className="text-xs text-muted-foreground">Service</p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {shipment.scac_code && (
                      <span className="rounded bg-muted px-1 py-0.5 font-mono text-xs text-muted-foreground">
                        {shipment.scac_code}
                      </span>
                    )}
                    {shipment.service_level && (
                      <Badge
                        variant={shipment.service_level.toLowerCase().includes('guaranteed') ? 'default' : 'outline'}
                        className={`text-xs ${shipment.service_level.toLowerCase().includes('guaranteed') ? 'bg-green-600 hover:bg-green-700' : ''}`}
                      >
                        {shipment.service_level}
                      </Badge>
                    )}
                  </div>
                </div>
              )}
              {!po && shipment.carrier_name && (
                <div className="flex items-start gap-2">
                  <Truck className="mt-0.5 h-3.5 w-3.5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Carrier</p>
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium">{shipment.carrier_name}</p>
                      {shipment.scac_code && (
                        <span className="rounded bg-muted px-1 py-0.5 font-mono text-xs text-muted-foreground">
                          {shipment.scac_code}
                        </span>
                      )}
                    </div>
                    {shipment.service_level && (
                      <Badge
                        variant={shipment.service_level.toLowerCase().includes('guaranteed') ? 'default' : 'outline'}
                        className={`mt-1 text-xs ${shipment.service_level.toLowerCase().includes('guaranteed') ? 'bg-green-600 hover:bg-green-700' : ''}`}
                      >
                        {shipment.service_level}
                      </Badge>
                    )}
                  </div>
                </div>
              )}
              {shipment.miles_in_transit && (
                <div className="flex items-start gap-2">
                  <Route className="mt-0.5 h-3.5 w-3.5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Miles</p>
                    <p className="text-sm font-medium">{Number(shipment.miles_in_transit).toLocaleString()} mi</p>
                  </div>
                </div>
              )}
              {!po && (
                <>
              {shipment.tracking_numbers && (
                <div>
                  <p className="text-xs text-muted-foreground">Tracking</p>
                  <p className="text-sm font-mono">{shipment.tracking_numbers}</p>
                </div>
              )}
              {shipment.pro_number && (
                <div>
                  <p className="text-xs text-muted-foreground">PRO Number</p>
                  <p className="text-sm font-mono">{shipment.pro_number}</p>
                </div>
              )}
              {shipment.bol_number && (
                <div>
                  <p className="text-xs text-muted-foreground">BOL Number</p>
                  <p className="text-sm font-mono">{shipment.bol_number}</p>
                </div>
              )}
              {shipment.bol_reference && (
                <div>
                  <p className="text-xs text-muted-foreground">Supplier's BOL Reference</p>
                  <p className="text-sm font-mono">{shipment.bol_reference}</p>
                </div>
              )}
              {shipment.pickup_number && (
                <div>
                  <p className="text-xs text-muted-foreground">Pickup Number</p>
                  <p className="text-sm font-mono">{shipment.pickup_number}</p>
                </div>
              )}
                </>
              )}
              {shipment.booking_reference && (
                <div>
                  <p className="text-xs text-muted-foreground">Booking Reference</p>
                  <p className="text-sm font-mono">{shipment.booking_reference}</p>
                </div>
              )}
              {shipment.shipping_cost != null && (
                <div>
                  <p className="text-xs text-muted-foreground">Shipping Cost</p>
                  <div className="flex items-center gap-1.5">
                    <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                    <p className="text-sm font-bold">{formatMoney(shipment.shipping_cost)}</p>
                  </div>
                  {shipment.pricing_breakdown && (() => {
                    try {
                      const pb = JSON.parse(shipment.pricing_breakdown) as Record<string, number>
                      const freight = pb.FreightCharge ?? pb.freight_charge
                      const fuel = pb.FuelSurcharge ?? pb.fuel_surcharge
                      if (freight != null || fuel != null) {
                        return (
                          <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                            {freight != null && <p>Freight: {formatMoney(freight)}</p>}
                            {fuel != null && <p>Fuel: {formatMoney(fuel)}</p>}
                          </div>
                        )
                      }
                    } catch {
                      // ignore parse errors
                    }
                    return null
                  })()}
                </div>
              )}
              {shipment.booked_at && (
                <div>
                  <p className="text-xs text-muted-foreground">Booked At</p>
                  <p className="text-sm font-medium">
                    {parseUTCDate(shipment.booked_at).toLocaleString()}
                  </p>
                </div>
              )}
              {!shipment.carrier_name &&
                !shipment.tracking_numbers &&
                !shipment.pro_number &&
                !shipment.shipping_cost && (
                  <p className="text-sm text-muted-foreground">No logistics info yet</p>
                )}
            </div>
            {(expectedPayable || (shipment.booked_by === 'us' && shipment.shipping_cost != null)) && (
              <section className="mt-4 border-t pt-4" aria-labelledby="expected-payable-heading">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-start gap-2">
                    <ReceiptText className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    <div>
                      <h2 id="expected-payable-heading" className="text-sm font-medium">
                        Freight reconciliation
                      </h2>
                    </div>
                  </div>
                  <Badge
                    variant={
                      currentFreightVariance?.status === 'proposed' ||
                      currentFreightVariance?.status === 'rejected'
                        ? 'destructive'
                        : 'secondary'
                    }
                  >
                    {currentFreightVariance?.status === 'accepted'
                      ? 'Reconciled'
                      : currentFreightVariance?.status === 'proposed'
                        ? 'Needs review'
                        : currentFreightVariance?.status === 'rejected'
                          ? 'Adjustment requested'
                          : currentAllocations.length
                            ? 'Needs review'
                            : 'Pending invoice'}
                  </Badge>
                </div>

                {expectedPayable && (
                  <div className="mt-3 space-y-3">
                    <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-md border bg-border sm:grid-cols-4">
                      {[
                        ['Selected quote', expectedPayable.selected_quote_amount_snapshot],
                        ['Booked', expectedPayable.amount],
                        ['Carrier invoice', currentAllocations.length ? carrierInvoiceTotal : null],
                        ['Variance', currentFreightVariance?.variance_amount],
                      ].map(([label, value]) => (
                        <div key={String(label)} className="bg-background px-3 py-2">
                          <dt className="text-xs text-muted-foreground">{label}</dt>
                          <dd className="mt-0.5 font-mono text-sm font-semibold tabular-nums">
                            {value == null ? '—' : formatMoney(value)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                    {payableAllocations.length === 0 ? (
                      <p className="rounded-md bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                        No AP invoices allocated
                      </p>
                    ) : (
                      <ul className="space-y-2" aria-label="AP invoice allocations">
                        {[...payableAllocations]
                          .sort(
                            (a, b) =>
                              parseUTCDate(b.created_at).getTime() -
                              parseUTCDate(a.created_at).getTime(),
                          )
                          .map((allocation) => (
                            <li
                              key={allocation.uuid}
                              className="flex flex-col gap-2 rounded-md bg-muted/40 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                            >
                              <div className="min-w-0">
                                <Link
                                  to="/invoices/$invoiceId"
                                  params={{ invoiceId: allocation.invoice.uuid }}
                                  className="text-sm font-medium hover:underline"
                                >
                                  {allocation.invoice.invoice_number
                                    ? `Invoice ${allocation.invoice.invoice_number}`
                                    : 'AP invoice'}
                                </Link>
                                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                  <Badge variant="outline" className="text-[11px]">
                                    {formatStatus(allocation.review_status)}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">
                                    {formatStatus(allocation.source)}
                                  </span>
                                  {allocation.confidence != null && (
                                    <span className="text-xs text-muted-foreground">
                                      {Math.round(Number(allocation.confidence) * 100)}% confidence
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span className="font-mono text-sm font-semibold tabular-nums">
                                {formatMoney(allocation.allocation_amount)}
                              </span>
                            </li>
                          ))}
                      </ul>
                    )}
                    {expectedPayable.status === 'cancelled' && expectedPayable.cancellation_reason && (
                      <p className="text-xs text-muted-foreground">
                        {expectedPayable.cancellation_reason}
                      </p>
                    )}
                  </div>
                )}
              </section>
            )}
            {(shipment.bol_pdf_filename || shipment.label_pdf_filename) && (
              <div className="mt-4 flex gap-2 border-t pt-4">
                {shipment.bol_pdf_filename && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="cursor-pointer"
                    onClick={() =>
                      handleDownloadDocument('bol', shipment.bol_pdf_filename!)
                    }
                  >
                    <Download className="mr-1.5 h-3.5 w-3.5" />
                    Download BOL
                  </Button>
                )}
                {shipment.label_pdf_filename && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="cursor-pointer"
                    onClick={() =>
                      handleDownloadDocument('label', shipment.label_pdf_filename!)
                    }
                  >
                    <Download className="mr-1.5 h-3.5 w-3.5" />
                    Download Label
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tracking Timeline */}
      {shipment.tracking_events && shipment.tracking_events.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Tracking Timeline</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="relative space-y-5 border-l border-muted-foreground/20 pl-6">
              {[...shipment.tracking_events]
                .sort((a, b) => {
                  const at = a.event_time
                    ? parseUTCDate(a.event_time).getTime()
                    : parseUTCDate(a.created_at).getTime()
                  const bt = b.event_time
                    ? parseUTCDate(b.event_time).getTime()
                    : parseUTCDate(b.created_at).getTime()
                  return bt - at
                })
                .map((e, i) => {
                  const cfg = trackingStatusStyle(e.status)
                  return (
                    <li key={e.id} className="relative">
                      <span
                        className={`absolute -left-[31px] mt-1 flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-background ${cfg.dot}`}
                      >
                        {i === 0 && <span className="h-2 w-2 rounded-full bg-white" />}
                      </span>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className={cfg.badge}>
                          {formatStatus(e.status)}
                        </Badge>
                        {e.event_code && e.event_code !== e.status.toUpperCase() && (
                          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                            {e.event_code}
                          </span>
                        )}
                        {e.event_time && (
                          <span className="text-xs text-muted-foreground">
                            {parseUTCDate(e.event_time).toLocaleString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })}
                          </span>
                        )}
                      </div>
                      {e.description && <p className="mt-1 text-sm">{e.description}</p>}
                      {e.location && (
                        <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          {e.location}
                        </p>
                      )}
                    </li>
                  )
                })}
            </ol>
          </CardContent>
        </Card>
      )}

      {/* Ship From / Ship To addresses side by side */}
      {(hasShipFromAddress || hasShipToAddress) && (
        <div className="grid gap-6 md:grid-cols-2">
          {hasShipFromAddress && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Ship From</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm space-y-0.5">
                  {shipment.ship_from_name && <p className="font-medium">{shipment.ship_from_name}</p>}
                  {shipment.ship_from_contact_name && (
                    <p className="text-muted-foreground">{shipment.ship_from_contact_name}</p>
                  )}
                  {shipment.ship_from_address1 && <p>{shipment.ship_from_address1}</p>}
                  {shipment.ship_from_address2 && <p>{shipment.ship_from_address2}</p>}
                  {(shipment.ship_from_city || shipment.ship_from_state || shipment.ship_from_zip) && (
                    <p>
                      {[shipment.ship_from_city, shipment.ship_from_state].filter(Boolean).join(', ')}
                      {shipment.ship_from_zip ? ` ${shipment.ship_from_zip}` : ''}
                    </p>
                  )}
                  {shipment.ship_from_country && shipment.ship_from_country !== 'US' && shipment.ship_from_country !== 'USA' && (
                    <p>{shipment.ship_from_country}</p>
                  )}
                  {shipment.ship_from_phone && (
                    <p className="text-muted-foreground">Phone: {shipment.ship_from_phone}</p>
                  )}
                  {shipment.ship_from_email && (
                    <p className="text-muted-foreground">{shipment.ship_from_email}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          {hasShipToAddress && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Ship To</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm space-y-0.5">
                  {po?.ship_to_name && <p className="font-medium">{po.ship_to_name}</p>}
                  {po?.ship_to_company && <p>{po.ship_to_company}</p>}
                  {po?.ship_to_address1 && <p>{po.ship_to_address1}</p>}
                  {po?.ship_to_address2 && <p>{po.ship_to_address2}</p>}
                  {(po?.ship_to_city || po?.ship_to_state || po?.ship_to_zip) && (
                    <p>
                      {[po?.ship_to_city, po?.ship_to_state].filter(Boolean).join(', ')}
                      {po?.ship_to_zip ? ` ${po.ship_to_zip}` : ''}
                    </p>
                  )}
                  {po?.ship_to_country && po.ship_to_country !== 'US' && po.ship_to_country !== 'USA' && (
                    <p>{po.ship_to_country}</p>
                  )}
                  {po?.ship_to_phone && (
                    <p className="text-muted-foreground">Phone: {po.ship_to_phone}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Freight Details & Handling Units */}
      {(shipment.pallet_count || shipment.total_weight_lbs || shipment.freight_class || shipment.nmfc_code || shipment.handling_units?.length) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Freight Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {(shipment.pallet_count || shipment.total_weight_lbs || shipment.freight_class || shipment.nmfc_code) && (
              <div className="flex flex-wrap gap-4 rounded-md bg-muted/50 px-3 py-2 text-sm">
                {shipment.pallet_count != null && (
                  <div className="flex items-center gap-1.5">
                    <Package className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{shipment.pallet_count} pallet{shipment.pallet_count > 1 ? 's' : ''}</span>
                  </div>
                )}
                {shipment.total_weight_lbs != null && (
                  <span>{Number(shipment.total_weight_lbs).toLocaleString()} lbs</span>
                )}
                {shipment.freight_class && <span>Class {shipment.freight_class}</span>}
                {shipment.nmfc_code && <span>NMFC: {shipment.nmfc_code}</span>}
                {shipment.average_density && (
                  <div className="flex items-center gap-1.5">
                    <Scale className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{Number(shipment.average_density).toFixed(2)} lbs/ft³</span>
                  </div>
                )}
                {shipment.total_volume_cuft && (
                  <div className="flex items-center gap-1.5">
                    <BarChart3 className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{Number(shipment.total_volume_cuft).toFixed(1)} ft³</span>
                  </div>
                )}
              </div>
            )}

            {/* Handling Units */}
            {shipment.handling_units?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Handling Units</p>
                <div className="space-y-2">
                  {shipment.handling_units.map((hu) => (
                    <div key={hu.id} className="rounded-md border px-3 py-2">
                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        <span className="font-medium">
                          {hu.num_units}x {hu.unit_type}
                        </span>
                        {(hu.length_in || hu.width_in || hu.height_in) && (
                          <span className="text-muted-foreground">
                            {[hu.length_in, hu.width_in, hu.height_in].filter(v => v != null).join(' × ')}&quot;
                          </span>
                        )}
                        {hu.stackable && (
                          <Badge variant="outline" className="text-xs">Stackable</Badge>
                        )}
                      </div>
                      {hu.commodities?.length > 0 && (
                        <div className="mt-1.5 space-y-1 pl-3 border-l-2 border-muted">
                          {hu.commodities.map((c) => (
                            <div key={c.id} className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                              <span>{c.description}</span>
                              <span>{Number(c.weight_lbs).toLocaleString()} lbs</span>
                              {c.pieces > 1 && <span>{c.pieces} pcs</span>}
                              {c.freight_class && <span>Class {c.freight_class}</span>}
                              {c.nmfc_code && <span>NMFC {c.nmfc_code}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {shipment.dock_hours && (
              <p className="text-sm text-muted-foreground">Dock Hours: {shipment.dock_hours}</p>
            )}
            {shipment.dimensions_notes && (
              <p className="text-sm text-muted-foreground">{shipment.dimensions_notes}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Accessorials */}
      {(pickupAccessorials.length > 0 || deliveryAccessorials.length > 0) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Accessorials</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pickupAccessorials.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Pickup Requirements</p>
                <div className="flex flex-wrap gap-1.5">
                  {pickupAccessorials.map((a) => (
                    <Badge key={a} variant="outline" className="text-xs">{a}</Badge>
                  ))}
                </div>
              </div>
            )}
            {deliveryAccessorials.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Delivery Requirements</p>
                <div className="flex flex-wrap gap-1.5">
                  {deliveryAccessorials.map((a) => (
                    <Badge key={a} variant="outline" className="text-xs">{a}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Shipping Notes */}
      {shipment.shipping_notes && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Shipping Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{shipment.shipping_notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Carrier Quotes */}
      {shipment.quotes && shipment.quotes.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Carrier Quotes</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* Desktop table */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Carrier</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">Freight</TableHead>
                    <TableHead className="text-right">Fuel</TableHead>
                    <TableHead className="text-right">Transit</TableHead>
                    <TableHead className="text-right">Est. Delivery</TableHead>
                    <TableHead className="text-right">Miles</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...shipment.quotes]
                    .sort((a, b) => Number(a.total_price) - Number(b.total_price))
                    .map((quote) => (
                      <TableRow
                        key={quote.id}
                        className={quote.is_selected ? 'bg-green-50 dark:bg-green-950/20' : ''}
                      >
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {quote.is_selected && (
                              <Check className="h-3.5 w-3.5 shrink-0 text-green-600" />
                            )}
                            <div>
                              <p className="text-sm font-medium leading-tight">{quote.carrier_name}</p>
                              <span className="rounded bg-muted px-1 py-0.5 font-mono text-xs text-muted-foreground">
                                {quote.scac_code}
                              </span>
                            </div>
                            {quote.is_selected && (
                              <Badge variant="outline" className="ml-1 border-green-600 text-xs text-green-600">
                                Selected
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={quote.service_level.toLowerCase().includes('guaranteed') ? 'default' : 'outline'}
                            className={`text-xs ${quote.service_level.toLowerCase().includes('guaranteed') ? 'bg-green-600 hover:bg-green-700' : ''}`}
                          >
                            {quote.service_level}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {formatMoney(Number(quote.total_price))}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {quote.freight_charge ? formatMoney(Number(quote.freight_charge)) : '-'}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {quote.fuel_surcharge ? formatMoney(Number(quote.fuel_surcharge)) : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {quote.transit_days != null ? `${quote.transit_days}d` : '-'}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {quote.estimated_delivery_date
                            ? formatDateOnly(quote.estimated_delivery_date)
                            : '-'}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {quote.miles_in_transit
                            ? `${Number(quote.miles_in_transit).toLocaleString()} mi`
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile cards */}
            <div className="space-y-2 p-3 md:hidden">
              {[...shipment.quotes]
                .sort((a, b) => Number(a.total_price) - Number(b.total_price))
                .map((quote) => (
                  <div
                    key={quote.id}
                    className={`rounded-lg border p-3 ${
                      quote.is_selected
                        ? 'border-green-600 bg-green-50 dark:bg-green-950/20'
                        : 'bg-card'
                    }`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {quote.is_selected && (
                            <Check className="h-3.5 w-3.5 shrink-0 text-green-600" />
                          )}
                          <p className="break-words text-sm font-medium leading-tight">
                            {quote.carrier_name}
                          </p>
                          {quote.is_selected && (
                            <Badge variant="outline" className="border-green-600 text-xs text-green-600">
                              Selected
                            </Badge>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          <span className="rounded bg-muted px-1 py-0.5 font-mono text-[11px] text-muted-foreground">
                            {quote.scac_code}
                          </span>
                          <Badge
                            variant={
                              quote.service_level.toLowerCase().includes('guaranteed')
                                ? 'default'
                                : 'outline'
                            }
                            className={`text-xs ${
                              quote.service_level.toLowerCase().includes('guaranteed')
                                ? 'bg-green-600 hover:bg-green-700'
                                : ''
                            }`}
                          >
                            {quote.service_level}
                          </Badge>
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-base font-bold tabular-nums">
                          {formatMoney(Number(quote.total_price))}
                        </p>
                        {quote.transit_days != null && (
                          <p className="text-xs text-muted-foreground">{quote.transit_days}d transit</p>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 border-t pt-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Freight</span>
                        <span className="tabular-nums">
                          {quote.freight_charge ? formatMoney(Number(quote.freight_charge)) : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Fuel</span>
                        <span className="tabular-nums">
                          {quote.fuel_surcharge ? formatMoney(Number(quote.fuel_surcharge)) : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Est. Delivery</span>
                        <span className="tabular-nums">
                          {quote.estimated_delivery_date
                            ? formatDateOnly(quote.estimated_delivery_date)
                            : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Miles</span>
                        <span className="tabular-nums">
                          {quote.miles_in_transit
                            ? `${Number(quote.miles_in_transit).toLocaleString()} mi`
                            : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* WWEX Details */}
      {shipment.wwex_product_transaction_id && (
        <Card>
          <CardHeader className="cursor-pointer pb-3" onClick={() => setWwexDetailsOpen((v) => !v)}>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">WWEX Details</CardTitle>
              {wwexDetailsOpen ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
          </CardHeader>
          {wwexDetailsOpen && (
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                {(shipment.origin_terminal_code || shipment.dest_terminal_code) && (
                  <div className="flex items-start gap-2">
                    <MapPin className="mt-0.5 h-3.5 w-3.5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Terminals</p>
                      <p className="text-sm font-mono font-medium">
                        {shipment.origin_terminal_code || '—'} → {shipment.dest_terminal_code || '—'}
                      </p>
                    </div>
                  </div>
                )}
                {shipment.wwex_account_number && (
                  <div>
                    <p className="text-xs text-muted-foreground">Account Number</p>
                    <p className="text-sm font-mono font-medium">{shipment.wwex_account_number}</p>
                  </div>
                )}
                {shipment.wwex_product_transaction_id && (
                  <div>
                    <p className="text-xs text-muted-foreground">Transaction ID</p>
                    <p className="text-sm font-mono font-medium">{shipment.wwex_product_transaction_id}</p>
                  </div>
                )}
                {shipment.offer_expiration && (
                  <div>
                    <p className="text-xs text-muted-foreground">Offer Expires</p>
                    <p className="text-sm font-medium">
                      {parseUTCDate(shipment.offer_expiration).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Line Items */}
      {shipment.line_items?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Items in Shipment</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Desktop table */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[52px]" />
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shipment.line_items.map((li) => (
                    <TableRow key={li.id}>
                      <TableCell className="pr-0">
                        {li.line_item?.product?.image_url ? (
                          <img
                            src={li.line_item.product.image_url}
                            alt=""
                            className="h-10 w-10 rounded border object-cover"
                          />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded border bg-muted">
                            <Package className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="min-w-0 max-w-[520px] whitespace-normal">
                        <ProductSummaryRow
                          item={{
                            id: li.id,
                            title: li.line_item?.model,
                            description: li.line_item?.description,
                            productUuid: li.line_item?.product?.uuid,
                            imageUrl: null,
                            manufacturerName: li.line_item?.product?.manufacturer_name,
                            manufacturerUuid: li.line_item?.product?.manufacturer_uuid,
                          }}
                          imageClassName="hidden"
                        />
                      </TableCell>
                      <TableCell className="text-right">{li.quantity}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile cards */}
            <div className="space-y-2 md:hidden">
              {shipment.line_items.map((li) => (
                <div key={li.id} className="flex items-start gap-3 rounded-lg border bg-card p-2.5">
                  <div className="shrink-0">
                    {li.line_item?.product?.image_url ? (
                      <img
                        src={li.line_item.product.image_url}
                        alt=""
                        className="h-10 w-10 rounded border object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded border bg-muted">
                        <Package className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    {li.line_item?.product?.uuid ? (
                      <Link
                        to="/products/$productId"
                        params={{ productId: li.line_item.product.uuid }}
                        className="block break-words text-sm font-medium text-primary hover:underline"
                      >
                        {li.line_item?.model || '-'}
                      </Link>
                    ) : (
                      <span className="block break-words text-sm font-medium">{li.line_item?.model || '-'}</span>
                    )}
                    {li.line_item?.product?.manufacturer_name && (
                      <p className="text-xs text-muted-foreground">
                        {li.line_item.product.manufacturer_uuid ? (
                          <Link
                            to="/manufacturers/$manuId"
                            params={{ manuId: li.line_item.product.manufacturer_uuid }}
                            className="hover:underline hover:text-primary"
                          >
                            {li.line_item.product.manufacturer_name}
                          </Link>
                        ) : (
                          li.line_item.product.manufacturer_name
                        )}
                      </p>
                    )}
                    {li.line_item?.description && (
                      <p className="mt-0.5 break-words text-xs text-muted-foreground">{li.line_item.description}</p>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-semibold tabular-nums">×{li.quantity}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cancel Shipment Confirmation Dialog */}
      <Dialog
        open={cancelOpen}
        onOpenChange={(open) => {
          if (cancelMutation.isPending) return
          setCancelOpen(open)
          if (!open) {
            setCancelReason('')
            setCancelConfirmText('')
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Cancel LTL Shipment
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. Only proceed if the freight has not
              been picked up by the carrier.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
              <p className="mb-1 font-medium">What will happen:</p>
              <ul className="list-inside list-disc space-y-0.5">
                <li>
                  Shipment status will change to <strong>cancelled</strong>.
                </li>
                {shipment.status === 'booked' && shipment.wwex_product_transaction_id ? (
                  <li>
                    The carrier booking with{' '}
                    <strong>{shipment.carrier_name || 'the carrier'}</strong>{' '}
                    will be voided via WWEX (BOL{' '}
                    {shipment.bol_number ? (
                      <span className="font-mono">{shipment.bol_number}</span>
                    ) : (
                      '—'
                    )}
                    ).
                  </li>
                ) : (
                  <li>No carrier booking to void.</li>
                )}
                <li>A cancellation event is recorded on the PO timeline.</li>
              </ul>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cancel-reason" className="text-xs">
                Reason <span className="text-red-600">*</span>
              </Label>
              <ComposeTextarea
                id="cancel-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Why is this shipment being cancelled?"
                rows={3}
                disabled={cancelMutation.isPending}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cancel-confirm" className="text-xs">
                Type <span className="font-mono font-semibold">{confirmPhrase}</span> to
                confirm <span className="text-red-600">*</span>
              </Label>
              <ComposeInput
                id="cancel-confirm"
                value={cancelConfirmText}
                onChange={(e) => setCancelConfirmText(e.target.value)}
                placeholder={confirmPhrase}
                autoComplete="off"
                disabled={cancelMutation.isPending}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelOpen(false)}
              disabled={cancelMutation.isPending}
            >
              Keep Shipment
            </Button>
            <LoadingButton
              variant="destructive"
              loading={cancelMutation.isPending}
              loadingText="Cancelling..."
              disabled={!canSubmitCancel}
              onClick={handleConfirmCancel}
            >
              <XCircle className="mr-1.5 h-4 w-4" />
              Cancel Shipment
            </LoadingButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {shipment.purchase_order && (
        <EditShipmentSheet
          open={editOpen}
          onOpenChange={setEditOpen}
          poUuid={shipment.purchase_order.uuid}
          shipment={shipment}
          policyOnly={shipment.status !== 'pending' && shipment.status !== 'ready_to_book'}
        />
      )}
    </div>
  )
}
